<template>
	<div class="w-full max-w-8xl mx-auto">
		<div v-if="label || !sideButtons" class="flex w-full pb-4" :class="hideButtons ? 'justify-center' : 'justify-between'">
			<div v-if="label" class="flex text-ihh-midnight-gray text-xl lg:text-2xl font-semibold tracking-wide whitespace-nowrap">
				{{ label }}
				<div v-if="shopAll" class="flex w-full justify-end mt-2.5 sm:justify-start">
					<!-- <router-link :to="shopAll" class="ml-2 text-start text-ihh-dark-salmon underline text-xs font-semibold whitespace-nowrap">Shop all</router-link> -->
				</div>
			</div>

			<div v-if="!sideButtons && !childrenFit && !hideButtons" class="hidden sm:flex justify-end">
				<div class="flex space-x-2 mb-2">
					<div class="flex w-full justify-center text-center items-center font-semibold">
						<button
							:disabled="atStart"
							:class="{ 'opacity-30': atStart }"
							class="text-center items-center font-semibold transition duration-300 ease-in-out group -rotate-90 bg-ihh-salmon rounded-full cursor-pointer"
							@click="back"
						>
							<svg class="w-8 h-8 pb-[3px]" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
								<polygon points="50,35 75,60 72,63 50,42 28,63 25,60" class="fill-ihh-midnight-gray" />
							</svg>
						</button>
					</div>
					<div class="flex w-full justify-center text-center items-center font-semibold">
						<button
							class="text-center items-center font-semibold transition duration-300 ease-in-out group -rotate-90 bg-ihh-salmon rounded-full cursor-pointer"
							@click="next"
							:disabled="atEnd"
							:class="{ 'opacity-30 disabled:cursor-not-allowed': atEnd }"
						>
							<svg class="w-8 h-8 pt-[3px]" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
								<polygon points="50,65 75,40 72,37 50,58 28,37 25,40" class="fill-ihh-midnight-gray" />
							</svg>
						</button>
					</div>
				</div>
			</div>
		</div>

		<div class="flex w-full justify-start relative">
			<template v-if="sideButtons && !childrenFit && !hideButtons">
				<!-- Scroll Left Button -->
				<button
					aria-label="Scroll left"
					@click="back"
					class="rounded-lg bg-ihh-black/70 px-3 py-3 absolute left-0 z-10 top-1/2 transform -translate-y-1/2 text-white stroke-ihh-white rotate-180"
					:class="{ 'opacity-30': atStart }"
				>
					<svg class="w-3" viewBox="0 0 9 13" xmlns="http://www.w3.org/2000/svg">
						<path d="M1 1L7 6.5L1 12" stroke-width="1.75" fill="none" />
					</svg>
				</button>

				<!-- Scroll Right Button -->
				<button
					aria-label="Scroll right"
					@click="next"
					class="rounded-lg bg-ihh-black/70 px-3 py-3 absolute right-0 z-10 top-1/2 transform -translate-y-1/2 text-white stroke-ihh-white"
					:class="{ 'opacity-30 disabled:cursor-not-allowed': atEnd }"
				>
					<svg class="w-3" viewBox="0 0 9 13" xmlns="http://www.w3.org/2000/svg">
						<path d="M1 1L7 6.5L1 12" stroke-width="1.75" fill="none" />
					</svg>
				</button>
			</template>
			<div class="flex gap-4 overflow-x-auto w-full snap-x snap-mandatory no-scrollbar" ref="container" @scroll="calculateScroll">
				<slot>
					<Product
						v-for="p in products"
						:product="p"
						:key="p.id"
						class="snap-start w-[calc((100%-1rem)/2)] sm:w-[calc((100%-2rem)/3)] md:w-[calc((100%-3rem)/4)] lg:w-[calc((100%-5rem)/6)] shrink-0"
					/>
				</slot>
			</div>
		</div>

		<div v-if="showPages" class="hidden md:flex w-full justify-center mt-8 gap-x-2">
			<button v-for="page in pages" @click="scrollToPage(page)" class="w-3 h-3 rounded-full bg-ihh-gray-500 border-ihh-gray-500 border" />
		</div>
	</div>
</template>

<script>
import Product from '../../products/product.vue'

export default {
	components: { Product },
	props: ['products', 'label', 'shopAll', 'sideButtons', 'showPages', 'hideButtons'],
	data() {
		return {
			atStart: true,
			atEnd: false,
			childrenFit: false,
			observer: null,
		}
	},
	mounted() {
		this.calculateScroll()
		this.watchImages()

		// Create a MutationObserver to watch for changes
		this.observer = new MutationObserver(() => {
			this.calculateScroll()
			this.watchImages()
			this.$nextTick(() => this.calculateScroll())
		})

		// Start observing the slot container for changes
		if (this.$refs.container) {
			this.observer.observe(this.$refs.container, {
				childList: true, // Detect additions or removals of child elements
				subtree: true, // Detect changes to child elements within the slot
			})
		}
	},
	beforeUnmount() {
		// Disconnect the observer when the component is unmounted to avoid memory leaks
		if (this.observer) {
			this.observer.disconnect()
		}
	},
	watch: {
		products() {
			this.$nextTick(() => this.calculateScroll())
		},
	},
	methods: {
		next() {
			this.$refs.container.scrollBy({
				left: this.$refs.container.offsetWidth,
				behavior: 'smooth',
			})
		},
		back() {
			this.$refs.container.scrollBy({
				left: -this.$refs.container.offsetWidth,
				behavior: 'smooth',
			})
		},
		calculateScroll() {
			let containerWidth = this.$refs.container.offsetWidth
			let childrenTotalWidth = this.$refs.container.scrollWidth
			let childrenFit = containerWidth >= childrenTotalWidth
			this.childrenFit = childrenFit
			if (childrenFit) {
				this.atStart = true
				this.atEnd = true
			} else {
				this.atStart = this.$refs.container.scrollLeft === 0
				this.atEnd = this.$refs.container.scrollLeft + this.$refs.container.offsetWidth >= this.$refs.container.scrollWidth
			}
		},
		watchImages() {
			const images = this.$refs.container.querySelectorAll('img')

			// Update the total number of images
			this.totalImages = images.length

			// Attach the load event listener to each image
			images.forEach((img) => {
				if (!img.dataset.loaded) {
					img.addEventListener('load', this.calculateScroll)
					img.dataset.loaded = true // Mark as processed to avoid adding multiple listeners
				}
			})
		},
	},
}
</script>

<style scoped>
/* Override default product sizing for popular products */
.popular-products-scroller :deep(.snap-start) {
	width: calc((100% - 1rem) / 2);
}

@media (min-width: 480px) {
	.popular-products-scroller :deep(.snap-start) {
		width: calc((100% - 1.5rem) / 2);
	}
}

@media (min-width: 640px) {
	.popular-products-scroller :deep(.snap-start) {
		width: calc((100% - 2rem) / 3);
	}
}

@media (min-width: 768px) {
	.popular-products-scroller :deep(.snap-start) {
		width: calc((100% - 3rem) / 4);
	}
}

@media (min-width: 1024px) {
	.popular-products-scroller :deep(.snap-start) {
		width: calc((100% - 4rem) / 5);
	}
}
</style>
